import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { FileDoneOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import { useUninvoicedBills } from "../../../../../hooks/useUninvoicedBills";
import { isCashNoReceiptBill } from "../../../../../lib/billFiscalization";
import { useUsers } from "../../../../../hooks/useUsers";
import { InvoiceClosedBillsTable } from "./InvoiceClosedBillsTable";
import { R } from "../../orderingTheme";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
} from "../../modalPrimitives";

interface Props {
  open: boolean;
  onClose: () => void;
  // Подаваме пълните BillHistory обекти — InvoiceCreateModal ползва items,
  // суми, ДДС и пр., вместо да fetch-ва отново по id.
  onConfirm: (bills: BillHistory[]) => void;
  /** `false` — фирмата е във „Фискално устройство": затворените сметки ги
   *  няма (BE отказва софтуерна фактура) — само защо. „Свободна фактура" и
   *  фискалната фактура за ТЕКУЩАТА сметка са редове в избора преди този
   *  модал (`InvoicingChoiceModal`). */
  softwareAllowed?: boolean;
}

export const InvoiceBillSelectionModal = ({
  open,
  onClose,
  onConfirm,
  softwareAllowed = true,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: fetched = [], isLoading, isError } = useUninvoicedBills({
    enabled: open && softwareAllowed,
  });
  // Закритите в брой без бон („Закриване на сметка(1)") нямат фискален
  // документ, срещу който да се издаде фактура — не ги предлагаме изобщо.
  const bills = useMemo(
    () => fetched.filter((b) => !isCashNoReceiptBill(b)),
    [fetched],
  );
  const { data: users = [] } = useUsers(undefined, undefined, undefined, { enabled: open });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setSearch("");
    }
  }, [open]);

  // Map waiter_id → display name (имена, fallback username, fallback id).
  const waiterName = useMemo(() => {
    const map = new Map<number, string>();
    users.forEach((u) => {
      map.set(u.id, u.names || u.username);
    });
    return (id: number | null): string => {
      if (id == null) return "—";
      return map.get(id) ?? `#${id}`;
    };
  }, [users]);

  const filtered = useMemo(() => {
    if (!search.trim()) return bills;
    const q = search.trim().toLowerCase();
    return bills.filter((b) => {
      const wn = waiterName(b.waiter_id).toLowerCase();
      return (
        String(b.id).includes(q) ||
        b.name.toLowerCase().includes(q) ||
        (b.payment_method_name?.toLowerCase().includes(q) ?? false) ||
        wn.includes(q)
      );
    });
  }, [bills, search, waiterName]);

  const allSelected =
    filtered.length > 0 && filtered.every((b) => selected.has(b.id));
  const someSelected = filtered.some((b) => selected.has(b.id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        filtered.forEach((b) => next.delete(b.id));
      } else {
        filtered.forEach((b) => next.add(b.id));
      }
      return next;
    });
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size === 0) return;
    const picked = bills.filter((b) => selected.has(b.id));
    onConfirm(picked);
  };

  // Клавиатурно поведение в стила на ClientsModal:
  // - Esc в search → чисти + blur. Esc извън search → затваря модала.
  // - Enter извън search → потвърждава.
  // - Произволна печатна клавиша на desktop, докато focus-ът не е в input →
  //   автоматично почва да пише в search-а и го фокусира (без click).
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (e.target instanceof HTMLInputElement) {
          setSearch("");
          (e.target as HTMLElement).blur?.();
          return;
        }
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Enter" && !(e.target instanceof HTMLInputElement)) {
        e.preventDefault();
        handleConfirm();
        return;
      }
      // Auto-typing: само на desktop и само ако focus-ът не е вече в input.
      if (!isDesktop) return;
      if (e.target instanceof HTMLInputElement) return;
      if (
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        e.preventDefault();
        setSearch((prev) => prev + e.key);
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () =>
      window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, onClose, selected, isDesktop]);

  const modalWidth = isMobile ? undefined : isTablet ? 720 : 960;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="inv-modal"
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "88vh",
        }}
      >
        <style>{`
          .inv-modal .row { transition: background 120ms; }
          .inv-modal .row:hover { background: ${R.brandTint}; }
          .inv-modal .row.is-selected { background: ${R.brandTint}; }
          .inv-modal input[type="text"] {
            width: 100%;
            height: 40px;
            padding: 4px 12px 4px 36px;
            background: ${R.surface};
            border: 1px solid ${R.border};
            border-radius: 10px;
            font-size: 13px;
            color: ${R.ink};
            outline: none;
            transition: border-color 120ms, box-shadow 120ms;
          }
          .inv-modal input[type="text"]:focus {
            border-color: ${R.brand};
            box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-brand) 12%, transparent);
          }
          .inv-modal input[type="text"]::placeholder {
            color: ${R.textMuted};
          }
        `}</style>

        <ModalHeader
          icon={<FileDoneOutlined />}
          title={t("invoicing.selectBillTitle")}
          subtitle={
            softwareAllowed
              ? t("invoicing.selectBillSubtitle")
              : t("fiscalInvoice.mode.selectSubtitle")
          }
          onClose={onClose}
          isNarrow={isNarrow}
        />

        {/* Search row */}
        <div
          style={{
            padding: isNarrow ? "12px 16px 8px" : "14px 24px 10px",
            background: R.surface,
          }}
        >
          <div style={{ position: "relative" }}>
            <SearchOutlined
              style={{
                position: "absolute",
                left: 12,
                top: 12,
                color: R.textMuted,
                fontSize: 14,
                pointerEvents: "none",
              }}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t("invoicing.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Затворените сметки; във „Фискално устройство" на тяхното място —
            защо ги няма (текстът на отказа на BE), със същите отстъпи */}
        {softwareAllowed ? (
          <InvoiceClosedBillsTable
            isNarrow={isNarrow}
            isLoading={isLoading}
            isError={isError}
            totalCount={bills.length}
            filtered={filtered}
            selected={selected}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleAll={toggleAll}
            onToggleOne={toggleOne}
            waiterName={waiterName}
          />
        ) : (
          <div className={isNarrow ? "px-4 pb-3" : "px-6 pb-3.5"}>
            <p className="m-0 rounded-[10px] border border-pos-line2 bg-pos-panel px-3 py-2.5 text-[12.5px] font-medium leading-[1.4] text-pos-soft">
              {t("fiscalInvoice.mode.softwareOff")}
            </p>
          </div>
        )}

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          {softwareAllowed ? (
            <PrimaryButton
              label={
                selected.size > 0
                  ? t("invoicing.confirmWithCount", { count: selected.size })
                  : t("invoicing.confirm")
              }
              onClick={handleConfirm}
              disabled={selected.size === 0}
              shortcut={isDesktop ? "⏎" : undefined}
            />
          ) : null}
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default InvoiceBillSelectionModal;
